import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, Ban, LogOut, ShieldCheck, ShieldX } from "lucide-react";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";

const SRC_META = [
  { key: "OFAC", label: "OFAC (SDN)" },
  { key: "UN", label: "CSNU (ONU)" },
  { key: "FATF", label: "GAFI (FATF)" },
  { key: "FBI", label: "FBI Most Wanted" },
  { key: "INTERPOL", label: "INTERPOL" },
  { key: "WB", label: "Banco Mundial" },
  { key: "NAMUS", label: "NamUs 2.0" },
  { key: "OS_PEP", label: "PEP · OpenSanctions" },
  { key: "OS_CRIME", label: "RCA · OpenSanctions" },
];

export default function TvView() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const load = () => api.get("/tv").then((r) => setData(r.data)).catch(() => {});
    load();
    const poll = setInterval(load, 30000);
    const clk = setInterval(() => setNow(new Date()), 1000);
    return () => { clearInterval(poll); clearInterval(clk); };
  }, []);

  const sources = { ...(data?.sources || {}), ...(data?.os || {}) };
  const stats = data?.stats || {};

  const statCards = [
    { key: "total", label: t("tv.total"), value: stats.total, cls: "text-white" },
    { key: "positives", label: t("tv.positives"), value: stats.matches, cls: "text-red-400" },
    { key: "negatives", label: t("tv.negatives"), value: stats.cleared, cls: "text-emerald-400" },
    { key: "today", label: t("tv.today"), value: stats.today, cls: "text-[#044CBC]" },
  ];

  return (
    <div className="min-h-screen bg-[#081426] text-white p-6 lg:p-10 space-y-8" data-testid="tv-view">
      <div className="flex items-start justify-between gap-4">
        <button onClick={() => navigate("/")} data-testid="tv-exit-button"
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/10 hover:bg-[#044CBC] text-sm font-bold transition-colors shrink-0">
          <LogOut className="w-4 h-4" /> {t("tv.exit")}
        </button>
        <div className="text-center">
          <h1 className="font-heading text-2xl lg:text-4xl font-extrabold tracking-tight">{t("tv.title")}</h1>
          <p className="text-sm text-slate-400 mt-1">{user?.tenant_name} · KYCFederal</p>
        </div>
        <div className="text-right shrink-0">
          <div className="font-mono text-2xl lg:text-3xl font-bold tabular-nums" data-testid="tv-clock">
            {now.toLocaleTimeString("es-PA", { hour12: false })}
          </div>
          <div className="flex items-center justify-end gap-1.5 text-[11px] font-bold text-emerald-400 mt-1">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> {t("tv.live")}
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">{t("tv.dbs")}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {SRC_META.map((s) => {
            const info = sources[s.key] || {};
            return (
              <div key={s.key} className="rounded-xl bg-white/5 border border-white/10 p-4" data-testid={`tv-src-${s.key}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 truncate">{s.label}</span>
                  <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${info.status === "ok" ? "bg-emerald-400" : info.status === "error" ? "bg-red-500" : "bg-amber-400"}`} data-testid={`tv-src-dot-${s.key}`} />
                </div>
                <div className="font-heading text-xl lg:text-2xl font-extrabold mt-2 tabular-nums">{(info.count || 0).toLocaleString()}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{t("tv.entries")}</div>
                <div className="text-[10px] text-slate-500 mt-1.5 truncate">{t("tv.updated")}: {info.last_update ? new Date(info.last_update).toLocaleString("es-PA", { timeZone: "America/Panama", hour12: false }) : "—"}</div>
              </div>
            );
          })}
          <div className="rounded-xl bg-white/5 border border-white/10 p-4" data-testid="tv-src-BLACKLIST">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 truncate">{t("tv.internal_bl")}</span>
              <Ban className="w-3.5 h-3.5 text-[#044CBC] shrink-0" />
            </div>
            <div className="font-heading text-xl lg:text-2xl font-extrabold mt-2 tabular-nums">{(data?.blacklist_active || 0).toLocaleString()}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">{t("tv.entries")}</div>
            <div className="text-[10px] text-slate-500 mt-1.5">{t("tv.internal_note")}</div>
          </div>
          <div className={`rounded-xl border p-4 ${data?.threats?.ntas_active ? "bg-red-900/40 border-red-500/60" : "bg-white/5 border-white/10"}`} data-testid="tv-src-NTAS">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 truncate">NTAS (DHS)</span>
              <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${data?.threats?.ntas_last_sync ? (data?.threats?.ntas_active ? "bg-red-500 animate-pulse" : "bg-emerald-400") : "bg-amber-400"}`} />
            </div>
            <div className="font-heading text-xl lg:text-2xl font-extrabold mt-2 tabular-nums">{data?.threats?.ntas_active ?? 0}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">{t("tv.ntas_active")} · {data?.threats?.ntas_total ?? 0} {t("tv.ntas_hist")}</div>
            <div className="text-[10px] text-slate-500 mt-1.5 truncate">{t("tv.updated")}: {data?.threats?.ntas_last_sync ? new Date(data.threats.ntas_last_sync).toLocaleString("es-PA", { timeZone: "America/Panama", hour12: false }) : "—"}</div>
          </div>
          <div className="rounded-xl bg-white/5 border border-white/10 p-4" data-testid="tv-src-KEV">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 truncate">Cyber Agency · CISA KEV</span>
              <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${data?.threats?.kev?.total ? "bg-emerald-400" : "bg-amber-400"}`} />
            </div>
            <div className="font-heading text-xl lg:text-2xl font-extrabold mt-2 tabular-nums">{(data?.threats?.kev?.total || 0).toLocaleString()}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">{t("tv.entries")} · <span className="text-red-400 font-bold">{data?.threats?.kev?.critical ?? 0}</span> {t("cy.critical").toLowerCase()}</div>
            <div className="text-[10px] text-slate-500 mt-1.5 truncate">{t("tv.updated")}: {data?.threats?.kev?.released ? new Date(data.threats.kev.released).toLocaleString("es-PA", { timeZone: "America/Panama", hour12: false }) : "—"}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {statCards.map((c) => (
          <div key={c.key} className="rounded-xl bg-white/5 border border-white/10 px-5 py-4" data-testid={`tv-stat-${c.key}`}>
            <div className={`font-heading text-3xl lg:text-4xl font-extrabold tabular-nums ${c.cls}`}>{(c.value ?? 0).toLocaleString()}</div>
            <div className="text-xs font-semibold text-slate-400 mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      <div>
        <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#044CBC]" /> {t("tv.recent")}
        </h2>
        <div className="rounded-xl bg-white/5 border border-white/10 divide-y divide-white/5 overflow-hidden" data-testid="tv-feed">
          {(data?.feed || []).length === 0 && <div className="p-8 text-center text-sm text-slate-500">{t("h.empty")}</div>}
          {(data?.feed || []).map((s) => (
            <div key={s.id} className="flex items-center gap-4 px-5 py-3" data-testid={`tv-feed-${s.consult_number}`}>
              {s.result === "match"
                ? <ShieldX className="w-5 h-5 text-red-400 shrink-0" />
                : <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0" />}
              <div className="flex-1 min-w-0">
                <span className="font-semibold text-sm truncate">{s.query}</span>
                <span className="text-[10px] text-slate-500 ml-2 uppercase">{s.module}</span>
              </div>
              <span className={`text-[10px] font-extrabold px-2.5 py-1 rounded-full shrink-0 ${s.result === "match" ? "bg-red-500/15 text-red-400" : "bg-emerald-500/15 text-emerald-400"}`}
                    data-testid={`tv-feed-result-${s.consult_number}`}>
                {s.result === "match" ? `${t("tv.positive")} (${s.match_count})` : t("tv.negative")}
              </span>
              <span className="hidden md:block font-mono text-[11px] text-slate-500 shrink-0">{s.consult_number}</span>
              <span className="text-[11px] text-slate-500 shrink-0 tabular-nums">{new Date(s.created_at).toLocaleString("es-PA", { timeZone: "America/Panama", hour12: false })}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
